import { Text } from '@thanx/uikit/text'
import type {
  AvailableReward,
  EscalationData,
  RecoveryConfig,
  ResolutionData,
  SandboxEscalationType,
} from 'api/recoveryAi.types'
import { buildTranslate } from 'locales'
import React, { useEffect } from 'react'
import { StyleObject, useStyletron } from 'styletron-react'
import { merchantTheme } from 'theme'
import { currencyFormatter } from 'utilities/formatters'
import { logger } from 'utils/analytics/datadog'
import {
  buildReasoningCardStyle,
  REASONING_BULLET_SIZE_PX,
  REASONING_LINE_HEIGHT_PX,
  REASONING_STEP_GAP_PX,
  REASONING_TITLE_MARGIN_PX,
} from './reasoningCardStyle'
import { TOUR_ANCHORS } from './tourAnchors'

const t = buildTranslate('recovery_ai.sandbox.decision_log')
// The wizard card's empty copy lives in the reasoning block, not here: the Figma reads
// "Your agent's thinking will appear here", which is the string that block already
// held. `decision_log.wizard_empty` ("The AI reasoning will appear here after the agent
// assesses a resolution.") is what this replaces.
const tReasoning = buildTranslate('recovery_ai.sandbox.reasoning')

type Props = {
  resolutionData: ResolutionData | null
  escalationData: EscalationData | null
  // AI-204: 'wizard' titles the panel "AI reasoning" (and its empty copy) to match
  // the onboarding Figma; 'default' (in-tab / standalone) keeps "Decision Log".
  variant?: 'default' | 'wizard'
  /**
   * Powers the tone step of the `wizard` variant's narrative, which names the
   * brand-voice source. Optional, and the step is DROPPED rather than guessed
   * when it is absent — the legacy in-tab shell renders the `default` field view
   * and passes no config.
   */
  //   context: brand_voice_mode ONLY. The posture the card reports comes from
  //   `resolutionData.generosity_posture` — the value the decision actually used
  //   — never from the live config, which the merchant can edit mid-session from
  //   the card beside this one.
  config?: Pick<RecoveryConfig, 'brand_voice_mode'>
  /**
   * Lets the make-good step claim the reward came from the approved list, by
   * matching `reward_id` rather than asserting it blind. Omitted ⇒ the step names
   * the reward without the provenance clause.
   */
  approvedRewards?: AvailableReward[]
}

const formatCents = (cents: number): string => currencyFormatter(cents / 100)

// Mirrors the same two locals in ReasoningChecklist.tsx — the card's shared chrome
// lives in reasoningCardStyle, but these two stayed local there, so they stay local
// here rather than diverging from the file this card has to match.
const BULLET_GAP_PX = 8
// Optically centers the check bullet against the first line of the step title.
const BULLET_OFFSET_PX = 2

// Merchant-facing explanation per tool-level escalation type. thanx-ai's stream
// relays its own finer-grained vocabulary (SandboxEscalationType) unmapped, so
// this is where those slugs become something a merchant can act on.
//
//   context: `Record<SandboxEscalationType, true>` keeps this table and the union
//   from drifting apart WITHIN the FE. It cannot detect backend drift — there is
//   no shared schema or codegen, so a type thanx-ai adds compiles fine here and
//   degrades to `fallback` at runtime, by design.
//   context: unlike utilities/translateEnum, an unknown value must NOT fall back
//   to the raw slug: this string is the body of a sentence, so "no_priced_items"
//   would read worse than the generic line. The envelope is parsed unvalidated
//   (useRecoveryChat.ts), so a value outside the union can physically arrive.
const ESCALATION_REASON_KEYS: Record<SandboxEscalationType, true> = {
  budget_exceeded: true,
  cant_resolve: true,
  hallucinated_offer: true,
  invalid_proposal: true,
  no_eligible_rewards: true,
  no_priced_items: true,
  no_rewards_available: true,
  talker_hard_escalation: true,
  missing_summary: true,
  appraiser_error: true,
  appraiser_timeout: true,
  tool_error: true,
}

const KNOWN_ESCALATION_REASONS: ReadonlySet<string> = new Set(
  Object.keys(ESCALATION_REASON_KEYS)
)

const isSandboxEscalationType = (
  value: string
): value is SandboxEscalationType => KNOWN_ESCALATION_REASONS.has(value)

// Exported so the suite can walk every modelled type: the Record above proves the
// table matches the union, but only a render proves each one has a locale string
// behind it. Derived through the type guard rather than an `as` narrowing of
// Object.keys, so nothing here asserts a type it has not actually checked.
export const SANDBOX_ESCALATION_TYPES: SandboxEscalationType[] = Object.keys(
  ESCALATION_REASON_KEYS
).filter(isSandboxEscalationType)

const escalationReasonKey = (type: string): string =>
  isSandboxEscalationType(type) ? type : 'fallback'

export const UNMODELLED_ESCALATION_LOG =
  'recovery_ai.sandbox_escalation_type_drift'

/**
 * Drift signal. thanx-ai owns this vocabulary and nothing on either side makes an
 * addition observable here: the runtime guard degrades an unmodelled value to the
 * generic line, correctly but silently. This is the only thing that turns that into
 * something queryable.
 *
 *   context: the whole point is that silence is the failure mode. Two review rounds
 *   on this card were spent reconciling the FE union against thanx-ai by hand, and
 *   the union was stale both times.
 *   context: keyed on the type so it fires once per distinct value, not per render.
 *   `escalationReasonKey` stays pure — logging from it would fire on every re-render.
 */
export const useEscalationTypeDriftLog = (escalationType?: string): void => {
  useEffect(() => {
    if (!escalationType || isSandboxEscalationType(escalationType)) return
    logger.warn(UNMODELLED_ESCALATION_LOG, { escalation_type: escalationType })
  }, [escalationType])
}

// Figma's empty-state mark is two small loops joined by a diagonal, which does not map
// onto an icon in the FontAwesome 4 set the app ships. `lightbulb-o` is the closest
// SEMANTIC match to "your agent's thinking will appear here" — swap it for the real
// asset once design names it. Decorative: the copy beside it carries the meaning.
const EMPTY_STATE_ICON = 'fa fa-lightbulb-o'
const EMPTY_STATE_ICON_SIZE_PX = 20
const EMPTY_STATE_PADDING_Y_PX = 46

const DryRunBadge = () => {
  const [css] = useStyletron()
  return (
    <span
      className={css({
        display: 'inline-block',
        backgroundColor: merchantTheme.colors.cayenne10,
        color: merchantTheme.colors.cayenne50,
        fontSize: '10px',
        fontWeight: 700,
        letterSpacing: '1px',
        textTransform: 'uppercase',
        padding: '2px 8px',
        borderRadius: '4px',
      })}
    >
      {t('dry_run')}
    </span>
  )
}

const DetailRow = ({ label, value }: { label: string; value: string }) => {
  const [css] = useStyletron()
  return (
    <div className={`d-flex flex-column ${css({ gap: '2px' })}`}>
      <Text.BodyText4 bold>{label}</Text.BodyText4>
      <Text.BodyText3 color="grey70">{value}</Text.BodyText3>
    </div>
  )
}

type NarrativeStep = { id: string; title: string; body: string }

// One bullet of the merchant-facing narrative: bold title, spaced em dash, prose.
//
//   context: the markup mirrors ReasoningChecklist.tsx rather than sharing a
//   component with it. That card is contractually frozen (scenarioReasoning.ts:6-12)
//   and is the sole carrier of the tour's `reasoning` anchor whenever it mounts, so
//   folding the two together would couple a frozen surface to a live one for ~20
//   lines of markup. If a third caller appears, extract then.
const NarrativeStepItem = ({ step }: { step: NarrativeStep }) => {
  const [css] = useStyletron()
  return (
    <li
      className={css({
        display: 'flex',
        alignItems: 'flex-start',
        gap: `${BULLET_GAP_PX}px`,
      })}
    >
      {/* Inverted bullet — a white disc under a teal check, since fa-check-circle
          draws the opposite (solid teal disc, punched-out check). Same stack as
          ReasoningChecklist: this card and that one swap at the phase flip, so a
          difference here reads to the merchant as the card changing. Decorative. */}
      <div
        className={css({
          position: 'relative',
          width: `${REASONING_BULLET_SIZE_PX}px`,
          height: `${REASONING_BULLET_SIZE_PX}px`,
          marginTop: `${BULLET_OFFSET_PX}px`,
          flex: '0 0 auto',
        })}
        aria-hidden="true"
      >
        <i
          className={`fa fa-circle ${css({
            position: 'absolute',
            top: 0,
            left: 0,
            color: merchantTheme.colors.white,
            fontSize: `${REASONING_BULLET_SIZE_PX}px`,
          })}`}
          aria-hidden="true"
        />
        <i
          className={`fa fa-check ${css({
            position: 'absolute',
            top: 0,
            left: 0,
            color: merchantTheme.colors.spearmint50,
            fontSize: `${REASONING_BULLET_SIZE_PX}px`,
          })}`}
          aria-hidden="true"
        />
      </div>
      {/* BodyText5 (12px) with an explicit line-height, and ONE colour for both
          halves — the Figma separates the bold title from its prose by weight, not
          colour, and grey70 is 4.49:1 on this card's wash where 12px needs 4.5:1. */}
      <Text.BodyText5
        tag="div"
        color="grey90"
        className={css({
          flex: 1,
          minWidth: 0,
          lineHeight: `${REASONING_LINE_HEIGHT_PX}px`,
        })}
      >
        <Text.BodyText5 bold color="grey90">
          {step.title}
        </Text.BodyText5>
        {t('steps.title_separator')}
        {step.body}
      </Text.BodyText5>
    </li>
  )
}

/**
 * Builds the live narrative from the terminal envelope. Deliberately shorter than
 * the guided checklist's four steps — asserting the missing beats would invent
 * claims the payload cannot support.
 *
 *   context: no "Research the customer & order" beat — thanx-ai's dossier renders
 *   "Not available yet." until nexus sends a v2 context (AI-750).
 *   context: an escalation drops the posture beat — the Bouncer can terminate a
 *   case before the Appraiser applies posture at all.
 */
const buildSteps = (
  resolutionData: ResolutionData | null,
  escalationData: EscalationData | null,
  config: Props['config'],
  approvedRewards: AvailableReward[] | undefined
): NarrativeStep[] => {
  const steps: NarrativeStep[] = []

  // Nothing to narrate until the agent reaches a terminal outcome. Without this
  // the tone step would render on its own, next to the "will appear here" empty
  // copy — describing a decision that has not happened.
  if (!resolutionData && !escalationData) return steps

  if (config) {
    steps.push({
      id: 'set_tone',
      title: t('steps.set_tone'),
      body:
        config.brand_voice_mode === 'custom_text'
          ? t('steps.set_tone_custom')
          : t('steps.set_tone_default'),
    })
  }

  // ESCALATION FIRST when both payloads are present, matching the single outcome
  // SimulatorEngine derives (resolveTerminalOutcome). The engine already guarantees
  // mutual exclusion, so this is defence — but the two must not encode opposite
  // rules, and claiming a reward was granted beside a locked "went to a human" chat
  // is the failure worth defending against twice.
  if (escalationData) {
    steps.push({
      id: 'outcome',
      title: t('steps.route_to_team'),
      body: t(
        `steps.escalation_reasons.${escalationReasonKey(
          escalationData.escalation_type
        )}`
      ),
    })
  } else if (resolutionData) {
    // The posture the decision ACTUALLY used, not the currently-saved setting —
    // the merchant can edit generosity mid-session from the card beside this one,
    // and the step would then describe a decision that never happened.
    const posture = t(`posture_values.${resolutionData.generosity_posture}`)
    steps.push({
      id: 'posture',
      title: t('steps.applied_posture', { posture }),
      body: t('steps.applied_posture_body'),
    })

    const isApproved = (approvedRewards ?? []).some(
      reward => reward.id === resolutionData.reward_id
    )
    steps.push({
      id: 'outcome',
      title: t('steps.chose_make_good'),
      body: isApproved
        ? t('steps.chose_make_good_body_approved', {
            reward: resolutionData.reward_name,
          })
        : t('steps.chose_make_good_body', {
            reward: resolutionData.reward_name,
          }),
    })
  }

  return steps
}

const DecisionLogPanel = ({
  resolutionData,
  escalationData,
  variant = 'default',
  config,
  approvedRewards,
}: Props) => {
  const [css] = useStyletron()

  useEscalationTypeDriftLog(escalationData?.escalation_type)

  const isWizard = variant === 'wizard'
  const titleText = isWizard ? t('wizard_title') : t('title')
  const emptyText = isWizard ? tReasoning('empty') : t('empty')

  const hasData = resolutionData || escalationData
  // Read the flag rather than inferring it from `hasData`. thanx-ai derives
  // dry_run per request (AI-590) and the consumer channel dispenses for real,
  // so a presence-only badge would stamp "DRY RUN" over a resolution where real
  // money moved — a status indicator lying in the dangerous direction.
  // (thanx-ai PR #140 review)
  const isDryRun = Boolean(resolutionData?.dry_run || escalationData?.dry_run)
  // ENG-980: the wizard's "AI reasoning" card never carries the badge. Onboarding
  // merchants are always simulating, so the stamp is noise there — and the Figma
  // has no badge. Outside the wizard it still tracks the flag exactly (AI-590).
  const showDryRunBadge = !isWizard && isDryRun

  const steps = isWizard
    ? buildSteps(resolutionData, escalationData, config, approvedRewards)
    : []

  // ENG-972: the wizard card is a mint-accented panel (Figma). Its chrome now comes
  // from reasoningCardStyle, shared with the ReasoningChecklist that swaps in for this
  // card during the guided phase — the two carried duplicate copies of these rules, so
  // any drift between them read as the card changing at the phase flip. The in-tab /
  // standalone "Decision Log" card keeps the plain white-on-grey20 treatment.
  const cardStyle: StyleObject = isWizard
    ? buildReasoningCardStyle()
    : {
        backgroundColor: 'white',
        border: `1px solid ${merchantTheme.colors.grey20}`,
        borderRadius: '8px',
        padding: '24px',
        // Grow into the remaining right-column height (the parent is a flex
        // column) so the log bottom-aligns with the scenario/chat panes instead
        // of resolving height:100% against the full column and overflowing.
        flexGrow: 1,
      }

  return (
    <div
      // The wizard card IS the tour's "AI reasoning" step target (ENG-969 removed
      // the sibling ReasoningPanel that used to carry that anchor), so it takes
      // the `reasoning` anchor there and keeps `decision-log` everywhere else.
      data-tour={isWizard ? TOUR_ANCHORS.reasoning : TOUR_ANCHORS.decisionLog}
      className={css(cardStyle)}
    >
      {/* The wizard title's gap comes from the shared constant so this card and the
          ReasoningChecklist that replaces it put their first row on the same baseline
          across the phase flip; the standalone log keeps the bootstrap `mb-m`. */}
      <div
        className={`d-flex align-items-center justify-content-between ${
          isWizard
            ? css({ marginBottom: `${REASONING_TITLE_MARGIN_PX}px` })
            : 'mb-m'
        }`}
      >
        {/* Figma drops the wizard title well below body scale — matching
            ReasoningChecklist, since one replaces the other. Still an h4, so the
            heading order is unchanged. The standalone decision log keeps its
            section-header treatment. */}
        {isWizard ? (
          <Text.BodyText5
            bold
            tag="h4"
            color="grey90"
            className={css({
              lineHeight: `${REASONING_LINE_HEIGHT_PX}px`,
              margin: 0,
            })}
          >
            {titleText}
          </Text.BodyText5>
        ) : (
          <Text.Header4 bold>{titleText}</Text.Header4>
        )}
        {showDryRunBadge && <DryRunBadge />}
      </div>

      {/* Empty state. The wizard's is centred under a glyph per the Figma; the
          standalone log keeps its plain left-aligned line. */}
      {!hasData &&
        (isWizard ? (
          <div
            className={css({
              textAlign: 'center',
              padding: `${EMPTY_STATE_PADDING_Y_PX}px 0`,
            })}
          >
            <i
              className={`${EMPTY_STATE_ICON} ${css({
                display: 'block',
                fontSize: `${EMPTY_STATE_ICON_SIZE_PX}px`,
                // Stays grey70 while the text below goes grey90: this glyph is
                // decorative and aria-hidden, so 1.4.3 does not reach it and a muted
                // mark beside readable copy is the intent. Not an oversight.
                color: merchantTheme.colors.grey70,
                marginBottom: '10px',
              })}`}
              aria-hidden="true"
            />
            <Text.BodyText5
              // grey90, not grey70: this sits on the card's #F2FAFA wash where grey70 is
              // 4.49:1 against the 4.5:1 that 12px needs. Same fix as the step prose.
              color="grey90"
              className={css({ lineHeight: `${REASONING_LINE_HEIGHT_PX}px` })}
            >
              {emptyText}
            </Text.BodyText5>
          </div>
        ) : (
          <Text.BodyText3 color="grey50">{emptyText}</Text.BodyText3>
        ))}

      {/* The merchant-facing narrative. Same beat structure as the guided
          checklist, built from the live envelope instead of frozen copy. */}
      {isWizard && steps.length > 0 && (
        /* Explicit role="list": Safari/VoiceOver drops the implicit list role
           when list-style is removed (WebKit bug 170179), so re-assert it to
           preserve the ordered-step semantics (WCAG 1.3.1). */
        <ol
          role="list"
          className={css({
            listStyle: 'none',
            margin: 0,
            padding: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: `${REASONING_STEP_GAP_PX}px`,
          })}
        >
          {steps.map(step => (
            <NarrativeStepItem key={step.id} step={step} />
          ))}
        </ol>
      )}

      {/* The `default` variant stays a raw field readout: it is the in-tab
          debugging view, where the un-narrated values ARE the point. No
          production shell renders it — both pass variant="wizard". */}
      {!isWizard && resolutionData && (
        <div className={`d-flex flex-column ${css({ gap: '12px' })}`}>
          <DetailRow
            label={t('reward_name')}
            value={resolutionData.reward_name}
          />
          <DetailRow
            label={t('reward_cost')}
            value={formatCents(resolutionData.reward_cost_cents)}
          />
          <DetailRow
            label={t('severity')}
            value={t(`severity_values.${resolutionData.severity_assessment}`)}
          />
          <DetailRow label={t('reasoning')} value={resolutionData.reasoning} />
          <DetailRow
            label={t('posture')}
            value={t(`posture_values.${resolutionData.generosity_posture}`)}
          />
          {/*
            Per-decision "Budget remaining" was removed: the value was computed as
            (daily_budget_cents - this_reward_cost) with no notion of cumulative
            same-day spend, which made it misleading once the agent dispenses more
            than once. An aggregate figure needs a real recovery_dispensations
            persistence layer. Tracked alongside the post-dry_run persistence
            design — see the Jira follow-up linked from the thanx-ai conventions doc.
          */}
        </div>
      )}

      {!isWizard && escalationData && (
        <div className={`d-flex flex-column ${css({ gap: '12px' })}`}>
          <DetailRow
            label={t('escalation_type')}
            value={escalationData.escalation_type}
          />
          <DetailRow
            label={t('escalation_reason')}
            value={escalationData.reason}
          />
          {/*
            "Phone number" was removed: thanx-ai never emits the field (no
            `phone_number` anywhere in its app/ or lib/), so the row rendered a
            labelled blank on every escalation for as long as it existed.
          */}
        </div>
      )}
    </div>
  )
}

export default DecisionLogPanel
